/**
 * ASCII Art Generator - 脉动效果计算
 *
 * 实现整体亮度、大小或透明度的脉动动画效果
 */

import type { PulseConfig } from './types'

/**
 * 计算脉动因子
 *
 * @param time 当前时间 (秒)
 * @param config 脉动配置
 * @returns 脉动因子 (0-2 范围，1 为基准)
 */
export function calculatePulseFactor(
  time: number,
  config: PulseConfig
): number {
  const { amplitude, frequency } = config

  // 使用正弦波产生脉动效果
  // 返回值范围: 1 - amplitude 到 1 + amplitude
  const pulse = Math.sin(time * frequency * Math.PI * 2)
  return 1 + pulse * amplitude
}

/**
 * 计算脉动亮度调整值
 *
 * @param baseBrightness 原始亮度 (0-255)
 * @param pulseFactor 脉动因子
 * @param amplitude 脉动振幅
 * @returns 调整后的亮度 (0-255)
 */
export function applyPulseToBrightness(
  baseBrightness: number,
  pulseFactor: number,
  amplitude: number
): number {
  // 将亮度根据脉动因子调整
  const adjusted = baseBrightness * (1 + (pulseFactor - 1) * amplitude)
  return Math.max(0, Math.min(255, Math.round(adjusted)))
}

/**
 * 脉动效果类
 */
export class PulseEffect {
  private config: PulseConfig
  private time = 0

  constructor(config: PulseConfig) {
    this.config = { type: 'brightness', ...config }
  }

  /**
   * 更新时间
   */
  update(deltaTime: number): void {
    this.time += deltaTime
  }

  /**
   * 获取当前脉动因子
   */
  getPulseFactor(): number {
    return calculatePulseFactor(this.time, this.config)
  }

  /**
   * 更新配置
   */
  setConfig(config: Partial<PulseConfig>): void {
    this.config = { ...this.config, ...config }
  }

  /**
   * 获取当前配置
   */
  getConfig(): PulseConfig {
    return { ...this.config }
  }

  /**
   * 重置时间
   */
  reset(): void {
    this.time = 0
  }
}

/**
 * 从渲染设置创建脉动配置
 */
export function createPulseConfigFromSettings(
  amplitude: number = 0.1,
  frequency: number = 0.5
): PulseConfig {
  return {
    amplitude,
    frequency,
    type: 'brightness'
  }
}